import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
  output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  IconComponent,
  MultiStateIndicatorComponent,
  StateIndicator,
  TooltipDirective,
} from '@ui';
import { IndicatorState } from '../../../models/indicator.models';
import { IndicatorStates } from '../../../constants/indicators.constants';

@Component({
  selector: 'ui-center-indicator',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconComponent,
    MultiStateIndicatorComponent,
    TooltipDirective,
    TranslocoDirective,
  ],
  template: `
    <ng-container *transloco="let t">
      <div [class]="states?.[state()]?.label" class="h-5 max-w-[85%] text-ellipsis whitespace-nowrap overflow-hidden">
        <span
          class="text-xs font-medium uppercase transition-colors duration-300"
          uiTooltip
          [tooltipTitle]="nameTemplate"
          (click)="emitCenter()"
        >
          {{ centerName() }}
        </span>
      </div>

      @if (states && indicatorsAmount()) {
        <div
          uiTooltip
          [tooltipTitle]="isDisabled() ? null : tooltipTitleTemplate"
          [tooltipTitleContext]="{ $implicit: { amount: indicatorsAmount() } }"
          class="w-fit"
        >
          <ui-multi-state-indicator
            [indicators]="indicators()"
            [states]="states"
          />
        </div>
      }
      <ng-template #transformerIconTemplate let-data>
        <ui-icon
          uiTooltip
          [tooltipTitle]="isDisabled() ? null : tooltipTitleTemplate"
          [tooltipTitleContext]="{ $implicit: data }"
          [tooltipTrigger]="this.isDisabled() ? null : 'hover'"
          icon="icons:trafo"
          [class]="
            isDisabled() ? 'text-neutral-6' : 'text-tertiary cursor-pointer'
          "
        />
      </ng-template>

      <ng-template #transformerIconDisabledTemplate>
        <ui-icon
          icon="icons:trafo"
          [class]="
            isDisabled() ? 'text-neutral-6' : 'text-gray-300 cursor-pointer'
          "
        />
      </ng-template>

      <ng-template #tooltipTitleTemplate>
        <div class="flex flex-col text-xs cursor-pointer">
          <span>
            {{ indicatorsAmount() }}
            {{
              indicatorsAmount() > 1
                ? t('DISTRIBUTION_NETWORK.TABLE.VIEW_TRANSFORMERS')
                : t('DISTRIBUTION_NETWORK.TABLE.VIEW_TRANSFORMER')
            }}
          </span>
        </div>
      </ng-template>

      <ng-template #nameTemplate>
        {{ centerName() }}
      </ng-template>
    </ng-container>
  `,
})
export class CenterIndicatorComponent implements AfterViewInit {
  private cdr = inject(ChangeDetectorRef);

  centerName = input.required<string>();
  distributorId = input.required<string>();
  centerId = input.required<string>();
  rsabtId = input.required<string>();
  vtnId = input.required<string>();
  state = input<IndicatorState>(IndicatorStates.Default);
  indicators = input<string[]>([]);
  onRsabt = output<string>();

  onCenter = output<{
    distributorId: string;
    centerId: string;
    rsabtId: string;
    vtnId: string;
  }>();

  isDisabled = computed<boolean>(
    () => this.state() === IndicatorStates.Disabled,
  );

  indicatorsAmount = computed(() => [...this.indicators()]?.length);

  @ViewChild('transformerIconTemplate')
  transformerIconTemplate!: TemplateRef<any>;
  @ViewChild('transformerIconDisabledTemplate')
  transformerIconDisabledTemplate!: TemplateRef<any>;

  states!: StateIndicator | undefined;

  ngAfterViewInit(): void {
    this.states = {
      active: {
        content: this.transformerIconTemplate,
        label: 'cursor-pointer text-primary hover:text-tertiary',
      },
      disabled: {
        content: this.transformerIconDisabledTemplate,
        label: 'text-neutral-6',
      },
    };
    this.cdr.detectChanges();
  }

  emitCenter() {
    this.onCenter.emit({
      distributorId: this.distributorId()!,
      centerId: this.centerId()!,
      rsabtId: this.rsabtId()!,
      vtnId: this.vtnId()!,
    });
  }

  emitRsabt(id: string) {
    this.onRsabt.emit(id);
  }
}
